import { createFileRoute } from "@tanstack/react-router";
import AssignmentLoading from "../components/loadings/assignment";
import { useEffect, useState } from "react";
import {
  ListChecks,
  Search,
  UserMinus,
  UserPlus,
  UsersRound,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  useDeleteData,
  useGetConditionalData,
  useGetData,
  usePostData,
} from "@/hooks/services/use-query-hooks";
import {
  AssignJobOrderTechnician,
  JobOrderDetail,
  UnAssignJobOrderTechnician,
} from "@/hooks/services/pps/handlers/job-orders-api";
import { TechnicianList } from "@/hooks/services/pps/handlers/technicians-api";

import {
  JobDetailResponse,
  TechnicianAssigmentPayload,
} from "@/hooks/services/pps/types/job-orders.type";
import {
  Technician,
  TechnicianResponse,
} from "@/hooks/services/pps/types/technicians.type";
import { useJobOrderStore, useWorkSpaceStore } from "@/stores/store";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getInitials } from "../utils/utils";
import { cn } from "@/lib/utils";

interface SelectedTechnician extends Technician {
  selected: boolean;
}

interface TechnicianAssignments extends Technician {
  unAssigned: boolean;
}

function SectionDivider({ title }: { title: string }) {
  return (
    <div className="aero:flex aero:items-center aero:gap-3 aero:w-full">
      <div className="aero:w-[3px] aero:h-6 aero:bg-brand-primary aero:rounded-full aero:flex-shrink-0" />
      <span className="aero:font-inter aero:font-bold aero:text-base aero:leading-6 aero:text-brand-primary aero:whitespace-nowrap">
        {title}
      </span>
      <div className="aero:flex-1 aero:h-[1px] aero:bg-neutral-200" />
    </div>
  );
}

export const Route = createFileRoute(
  "/(planner-screens)/job-order/$id/assignment",
)({
  component: RouteComponent,
  pendingComponent: AssignmentLoading,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { workspace } = useWorkSpaceStore();
  // const [open, setOpen] = useState(false);

  const jobOrderData = useGetData<JobDetailResponse>(JobOrderDetail(id));

  const [selectedFilters, setSelectedFilters] = useState<{
    componentIds: number[];
  }>({
    componentIds: [],
  });

  const technicianData = useGetConditionalData<TechnicianResponse>(
    TechnicianList({ ...selectedFilters, workspaceId: workspace?.id }),
  );

  const setJobOrderData = useJobOrderStore((state) => state.setJobOrderData);

  const assignMutation = usePostData<TechnicianAssigmentPayload, any>(
    AssignJobOrderTechnician(),
  );
  const unAssignMutation = useDeleteData<TechnicianAssigmentPayload, any>(
    UnAssignJobOrderTechnician(),
  );

  const [originTechnician, setOriginTechnician] = useState<any>({
    assignment: [],
    available: [],
  });
  const [selectedTechnicians, setSelectedTechnicians] = useState<
    SelectedTechnician[]
  >([]);
  const [technicianAssignments, setTechnicianAssignments] = useState<
    TechnicianAssignments[]
  >([]);

  useEffect(() => {
    if (jobOrderData.isSuccess && jobOrderData?.data?.status === "success") {
      setSelectedFilters({ componentIds: [] });
      setJobOrderData(jobOrderData?.data?.data);
    }
  }, [jobOrderData.isSuccess]);

  useEffect(() => {
    technicianData.refetch();
  }, [selectedFilters]);

  useEffect(() => {
    // if (technicianData.isSuccess && technicianData?.data?.status === 'success') {
    const technicians = technicianData?.data?.data;

    const availableTechnicians = technicians
      ?.filter(
        (technician) =>
          !jobOrderData?.data?.data?.ppsTechnicians?.some(
            (tech) => tech.id === technician.id,
          ),
      )
      .map((item) => ({ ...item, selected: false }));

    const assignedTechnicians = technicians
      ?.filter((technician) =>
        jobOrderData?.data?.data?.ppsTechnicians?.some(
          (tech) => tech.id === technician.id,
        ),
      )
      .map((item) => ({ ...item, unAssigned: false }));

    if (
      (availableTechnicians && availableTechnicians?.length > 0) ||
      (assignedTechnicians && assignedTechnicians?.length > 0)
    ) {
      setSelectedTechnicians(availableTechnicians as SelectedTechnician[]);
      setTechnicianAssignments(assignedTechnicians as TechnicianAssignments[]);
      setOriginTechnician({
        available: availableTechnicians,
        assignment: assignedTechnicians,
      });
    }

    // }
  }, [
    technicianData.data?.data,
    technicianData.isSuccess,
    jobOrderData.data?.data,
  ]);

  useEffect(() => {
    if (assignMutation.isSuccess || unAssignMutation.isSuccess) {
      jobOrderData.refetch();
    }
  }, [assignMutation, unAssignMutation]);

  const handleTechnicianSelection = (technician: SelectedTechnician) => {
    setSelectedTechnicians((prev: SelectedTechnician[]) => {
      return prev.map((technicianItem) => {
        if (technicianItem.id === technician.id) {
          return { ...technicianItem, selected: !technicianItem.selected };
        }
        return technicianItem;
      });
    });
  };

  const handleUnAssignTechinican = (technician: TechnicianAssignments) => {
    setTechnicianAssignments((prev) => {
      return prev.map((technicianItem) => {
        if (technicianItem.id === technician.id) {
          return { ...technicianItem, unAssigned: !technicianItem.unAssigned };
        }
        return technicianItem;
      });
    });
  };

  // const handleResetFilter = () => {
  //   setSelectedFilters({ componentIds: [] });
  // };

  if (jobOrderData.isLoading || technicianData.isLoading) {
    return <AssignmentLoading />;
  }

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:py-4 aero:h-[calc(100vh-120px)] ">
      {/* Page Title */}
      <div className="aero:flex aero:items-center aero:gap-3 aero:flex-shrink-0">
        <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
          <UsersRound className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
        </div>
        <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
          Technician Assignment
        </h1>
      </div>

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-6 aero:!shadow-none aero:flex-1 aero:min-h-0 aero:flex aero:flex-col">
        <CardContent className="aero:!p-0 aero:flex-1 aero:min-h-0 aero:flex aero:flex-col">
          <div className="aero:grid aero:grid-cols-1 aero:lg:grid-cols-12 aero:gap-6 aero:flex-1 aero:min-h-0">
            {/* Left Section - Assigned Technicians */}
            <div className="aero:lg:col-span-5 aero:flex aero:flex-col aero:gap-4 aero:min-h-0">
              <div className="aero:flex aero:items-center aero:justify-between">
                <SectionDivider title="Assigned Technicians" />
              </div>

              <div className="aero:flex aero:items-center aero:gap-2">
                <div className="aero:relative aero:flex-1">
                  <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:-translate-y-1/2 aero:h-4 aero:w-4 aero:text-neutral-400" />
                  <Input
                    placeholder="Search assigned technicians..."
                    className="aero:pl-9"
                    onChange={(e) => {
                      const filteredValue = originTechnician.assignment.filter(
                        (i: any) =>
                          i?.name
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()),
                      );
                      setTechnicianAssignments([...filteredValue]);
                    }}
                  />
                </div>
                <Button
                  intent="secondary"
                  design="outline"
                  size="md"
                  className="aero:h-10 aero:gap-2 aero:px-4"
                  disabled={
                    jobOrderData.isLoading ||
                    technicianData.isLoading ||
                    assignMutation.isPending ||
                    unAssignMutation.isPending
                  }
                  onClick={() => {
                    setTechnicianAssignments((prev) =>
                      prev.map((tech) => ({
                        ...tech,
                        unAssigned: !prev.every((i) => i.unAssigned),
                      })),
                    );
                  }}
                >
                  <ListChecks className="aero:h-5 aero:w-5" />
                  <span className="aero:font-bold aero:text-sm">
                    {technicianAssignments.length > 0 &&
                    technicianAssignments.every((tech) => tech.unAssigned)
                      ? "Deselect"
                      : "Select"}{" "}
                    All
                  </span>
                </Button>
                <Button
                  intent="custom"
                  design="solid"
                  size="md"
                  className="aero:h-10 aero:gap-2 aero:px-4 aero:bg-red-600 aero:hover:bg-red-700 aero:text-white"
                  disabled={
                    jobOrderData.isLoading ||
                    assignMutation.isPending ||
                    unAssignMutation.isPending ||
                    !technicianAssignments.some((t) => t.unAssigned)
                  }
                  onClick={() => {
                    if (technicianAssignments.length > 0) {
                      unAssignMutation.mutate({
                        jobOrderId: Number(id),
                        technicianIds: technicianAssignments
                          .filter((i) => i.unAssigned)
                          .map((i) => i.id),
                      });
                    }
                  }}
                >
                  <UserMinus className="aero:h-5 aero:w-5" />
                  <span className="aero:font-bold aero:text-sm">
                    {unAssignMutation.isPending ? "Unassigning..." : "Unassign"}
                  </span>
                </Button>
              </div>

              <div className="aero:h-[1px] aero:bg-neutral-200 aero:w-full" />

              <ScrollArea className="aero:flex-1 aero:min-h-0">
                {technicianAssignments?.length > 0 ? (
                  <ul className="aero:flex aero:flex-col aero:gap-2">
                    {technicianAssignments.map((technician) => (
                      <li
                        key={technician.id}
                        className={cn(
                          "aero:flex aero:items-center aero:justify-between aero:p-3",
                          "aero:rounded-lg aero:border aero:border-neutral-200",
                          "aero:hover:bg-neutral-50 aero:transition-colors aero:cursor-pointer",
                          technician.unAssigned &&
                            "aero:bg-brand-primary-50 aero:border-brand-primary-200",
                        )}
                        onClick={() => handleUnAssignTechinican(technician)}
                      >
                        <div className="aero:flex aero:items-center aero:gap-3">
                          <Checkbox
                            checked={technician.unAssigned}
                            disabled={
                              jobOrderData.isLoading ||
                              assignMutation.isPending ||
                              unAssignMutation.isPending
                            }
                            onClick={(e) => e.stopPropagation()}
                            onCheckedChange={() =>
                              handleUnAssignTechinican(technician)
                            }
                          />
                          <Avatar className="aero:h-9 aero:w-9 aero:bg-brand-primary-100">
                            <AvatarFallback className="aero:text-brand-primary-700 aero:font-medium aero:text-sm">
                              {getInitials(technician.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="aero:flex aero:flex-col">
                            <span className="aero:font-inter aero:font-medium aero:text-sm aero:text-foreground">
                              {technician.name}
                            </span>
                            {technician.role && (
                              <span className="aero:font-inter aero:text-xs aero:text-neutral-500">
                                {technician.role}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button
                          intent="secondary"
                          design="text"
                          size="iconSm"
                          disabled={
                            jobOrderData.isLoading ||
                            assignMutation.isPending ||
                            unAssignMutation.isPending
                          }
                          onClick={() =>
                            unAssignMutation.mutate({
                              jobOrderId: Number(id),
                              technicianIds: [technician.id],
                            })
                          }
                        >
                          <X className="aero:h-4 aero:w-4 aero:text-neutral-500" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="aero:flex aero:items-center aero:justify-center aero:h-32 aero:border aero:border-dashed aero:border-neutral-300 aero:rounded-lg">
                    <p className="aero:text-sm aero:text-neutral-500">
                      No technicians assigned yet.
                    </p>
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Right Section - Available Technicians */}
            <div className="aero:lg:col-span-7 aero:flex aero:flex-col aero:gap-4 aero:min-h-0">
              <div className="aero:flex aero:items-center aero:justify-between">
                <SectionDivider title="Available Technicians" />
              </div>

              <div className="aero:flex aero:items-center aero:gap-2">
                <div className="aero:relative aero:flex-1">
                  <Search className="aero:absolute aero:left-3 aero:top-1/2 aero:-translate-y-1/2 aero:h-4 aero:w-4 aero:text-neutral-400" />
                  <Input
                    placeholder="Search available technicians..."
                    className="aero:pl-9"
                    onChange={(e) => {
                      const filteredValue = originTechnician.available.filter(
                        (i: any) =>
                          i?.name
                            .toLowerCase()
                            .includes(e.target.value.toLowerCase()),
                      );
                      setSelectedTechnicians([...filteredValue]);
                    }}
                  />
                </div>
                <Button
                  intent="secondary"
                  design="outline"
                  size="md"
                  className="aero:h-10 aero:gap-2 aero:px-4"
                  disabled={
                    jobOrderData.isLoading ||
                    assignMutation.isPending ||
                    unAssignMutation.isPending
                  }
                  onClick={() =>
                    setSelectedTechnicians((prev) =>
                      prev.map((tech) => ({
                        ...tech,
                        selected: !prev.every((tech) => tech.selected),
                      })),
                    )
                  }
                >
                  <ListChecks className="aero:h-5 aero:w-5" />
                  <span className="aero:font-bold aero:text-sm">
                    {selectedTechnicians.every((tech) => tech.selected)
                      ? "Deselect"
                      : "Select"}{" "}
                    All
                  </span>
                </Button>
                <Button
                  intent="custom"
                  design="solid"
                  size="md"
                  className="aero:h-10 aero:gap-2 aero:px-4 aero:bg-brand-primary-700 aero:hover:bg-brand-primary-800 aero:text-white"
                  disabled={
                    jobOrderData.isLoading ||
                    assignMutation.isPending ||
                    unAssignMutation.isPending ||
                    !selectedTechnicians.some((t) => t.selected)
                  }
                  onClick={() =>
                    assignMutation.mutate({
                      jobOrderId: Number(id),
                      technicianIds: selectedTechnicians
                        .filter((i) => i.selected)
                        .map((i) => i.id),
                    })
                  }
                >
                  <UserPlus className="aero:h-5 aero:w-5" />
                  <span className="aero:font-bold aero:text-sm">
                    {assignMutation.isPending
                      ? "Assigning..."
                      : "Assign Selected"}
                  </span>
                </Button>
              </div>

              <div className="aero:h-[1px] aero:bg-neutral-200 aero:w-full" />

              <Tabs
                defaultValue="list"
                className="aero:w-full aero:flex-1 aero:min-h-0 aero:flex aero:flex-col"
              >
                <TabsList className="aero:w-auto aero:h-auto aero:p-1 aero:bg-neutral-100 aero:rounded-lg">
                  <TabsTrigger
                    value="list"
                    className={cn(
                      "aero:px-4 aero:py-1.5 aero:text-sm aero:font-medium aero:rounded-md",
                      "aero:data-[state=active]:bg-white aero:data-[state=active]:text-brand-primary-700 aero:data-[state=active]:shadow-sm",
                      "aero:data-[state=inactive]:text-neutral-600",
                    )}
                  >
                    List View
                  </TabsTrigger>
                  <TabsTrigger
                    value="grid"
                    className={cn(
                      "aero:px-4 aero:py-1.5 aero:text-sm aero:font-medium aero:rounded-md",
                      "aero:data-[state=active]:bg-white aero:data-[state=active]:text-brand-primary-700 aero:data-[state=active]:shadow-sm",
                      "aero:data-[state=inactive]:text-neutral-600",
                    )}
                  >
                    Grid View
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="list"
                  className="aero:mt-4 aero:flex-1 aero:min-h-0"
                >
                  <ScrollArea className="aero:h-full">
                    <ul className="aero:flex aero:flex-col aero:gap-2">
                      {(selectedTechnicians as SelectedTechnician[]).map(
                        (technician) => (
                          <li
                            key={technician.id}
                            className={cn(
                              "aero:flex aero:items-center aero:justify-between aero:p-3",
                              "aero:rounded-lg aero:border aero:border-neutral-200",
                              "aero:hover:bg-neutral-50 aero:transition-colors aero:cursor-pointer",
                              technician.selected &&
                                "aero:bg-brand-primary-50 aero:border-brand-primary-200",
                            )}
                            onClick={() =>
                              handleTechnicianSelection(technician)
                            }
                          >
                            <div className="aero:flex aero:items-center aero:gap-3">
                              <Checkbox
                                disabled={
                                  jobOrderData.isLoading ||
                                  unAssignMutation.isPending ||
                                  assignMutation.isPending
                                }
                                checked={technician.selected}
                                onClick={(e) => e.stopPropagation()}
                                onCheckedChange={() =>
                                  handleTechnicianSelection(technician)
                                }
                              />
                              <Avatar className="aero:h-9 aero:w-9 aero:bg-brand-primary-100">
                                <AvatarFallback className="aero:text-brand-primary-700 aero:font-medium aero:text-sm">
                                  {getInitials(technician.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="aero:flex aero:flex-col">
                                <span className="aero:font-inter aero:font-medium aero:text-sm aero:text-foreground">
                                  {technician.name}
                                </span>
                                {technician.role && (
                                  <span className="aero:font-inter aero:text-xs aero:text-neutral-500">
                                    {technician.role}
                                  </span>
                                )}
                              </div>
                            </div>
                            {technician.role && (
                              <Badge
                                variant="outline"
                                className="aero:text-xs aero:border-brand-primary-200 aero:text-brand-primary-700 aero:bg-brand-primary-50"
                              >
                                {technician.role}
                              </Badge>
                            )}
                          </li>
                        ),
                      )}
                    </ul>
                  </ScrollArea>
                </TabsContent>

                <TabsContent
                  value="grid"
                  className="aero:mt-4 aero:flex-1 aero:min-h-0"
                >
                  <ScrollArea className="aero:h-full">
                    <div className="aero:grid aero:grid-cols-2 aero:gap-3">
                      {(selectedTechnicians as SelectedTechnician[]).map(
                        (technician) => (
                          <div
                            key={technician.id}
                            className={cn(
                              "aero:p-4 aero:rounded-lg aero:border aero:border-neutral-200",
                              "aero:hover:bg-neutral-50 aero:transition-colors aero:cursor-pointer",
                              technician.selected &&
                                "aero:bg-brand-primary-50 aero:border-brand-primary-200",
                            )}
                            onClick={() =>
                              handleTechnicianSelection(technician)
                            }
                          >
                            <div className="aero:flex aero:items-center aero:gap-3">
                              <Checkbox
                                disabled={
                                  jobOrderData.isLoading ||
                                  unAssignMutation.isPending ||
                                  assignMutation.isPending
                                }
                                checked={technician.selected}
                                onClick={(e) => e.stopPropagation()}
                                onCheckedChange={() =>
                                  handleTechnicianSelection(technician)
                                }
                              />
                              <Avatar className="aero:h-10 aero:w-10 aero:bg-brand-primary-100">
                                <AvatarFallback className="aero:text-brand-primary-700 aero:font-medium aero:text-sm">
                                  {getInitials(technician.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="aero:flex aero:flex-col">
                                <span className="aero:font-inter aero:font-medium aero:text-sm aero:text-foreground">
                                  {technician.name}
                                </span>
                                {technician.role && (
                                  <Badge
                                    variant="outline"
                                    className="aero:text-xs aero:w-fit aero:mt-1 aero:border-brand-primary-200 aero:text-brand-primary-700 aero:bg-brand-primary-50"
                                  >
                                    {technician.role}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
